import { useEffect, useState } from "react";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { apiFetch } from "../lib/api.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { useToast } from "../components/Toast.js";
import { RecurrenceEditor, EMPTY_RECURRENCE } from "../components/RecurrenceEditor.js";
import type { RecurrenceValue } from "../components/RecurrenceEditor.js";
import {
  type BlackoutSpecV2,
  compileToBlackoutSpec,
  describeBlackoutSpec,
  seedFromBlackoutSpec,
} from "../lib/blackoutSpec.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type BlackoutSchedule = {
  id: string;
  name: string;
  description: string;
  spec: BlackoutSpecV2;
  created_at: string;
  updated_at: string;
};

type EditState = {
  id: string | null;
  name: string;
  description: string;
  recurrence: RecurrenceValue;
};

const EMPTY_EDIT: EditState = {
  id: null,
  name: "",
  description: "",
  recurrence: { ...EMPTY_RECURRENCE },
};

function isValid(edit: EditState): boolean {
  if (!edit.name.trim()) return false;
  const r = edit.recurrence;
  if (r.is_recurring && r.granularity === "daily" && r.byday.length === 0) return false;
  if (!r.start_date) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ScheduleBlackouts() {
  const confirm = useConfirm();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<BlackoutSchedule[]>([]);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ blackout_schedules: BlackoutSchedule[] }>(
        "/api/tenant/blackout-schedules"
      );
      setRows(data.blackout_schedules);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  function openCreate() { setEdit({ ...EMPTY_EDIT, recurrence: { ...EMPTY_RECURRENCE } }); }

  function openEdit(row: BlackoutSchedule) {
    setEdit({ id: row.id, name: row.name, description: row.description, recurrence: seedFromBlackoutSpec(row.spec) });
  }

  async function save() {
    if (!edit || !isValid(edit)) return;
    setBusy(true);
    try {
      const spec = compileToBlackoutSpec(edit.recurrence);
      if (edit.id) {
        await apiFetch(`/api/tenant/blackout-schedules/${encodeURIComponent(edit.id)}`, {
          method: "PATCH",
          body: { name: edit.name.trim(), description: edit.description.trim(), spec },
        });
      } else {
        await apiFetch("/api/tenant/blackout-schedules", {
          method: "POST",
          body: { name: edit.name.trim(), description: edit.description.trim(), spec },
        });
      }
      setEdit(null);
      await load();
      toast.push({ kind: "success", message: "Saved." });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  async function del(row: BlackoutSchedule) {
    const ok = await confirm.confirm({
      title: "Delete blackout schedule?",
      detail: `This deletes "${row.name}". Workflows using it will no longer be blocked.`,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    try {
      await apiFetch(`/api/tenant/blackout-schedules/${encodeURIComponent(row.id)}`, { method: "DELETE" });
      await load();
      toast.push({ kind: "success", message: "Deleted." });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  return (
    <PageLayout
      title="Blackout Schedules"
      description="Define time windows when scheduled workflows should not run."
      actions={
        <button className="adminNewLink" type="button" onClick={openCreate}>
          + New blackout schedule
        </button>
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        <div className="card adminList">
          {loading ? <LoadingState /> : null}
          {!loading && error ? <ErrorState detail={error} /> : null}
          {!loading && !error && rows.length === 0 ? (
            <div className="adminListEmpty">
              No blackout schedules defined. Create one to block workflows during maintenance windows, holidays, or off-hours.
            </div>
          ) : null}
          {!loading && !error && rows.length > 0 ? (
            <div className="tableWrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Schedule</th>
                    <th>Description</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td style={{ fontWeight: 500 }}>
                        <button
                          type="button"
                          className="btn btnLink"
                          onClick={() => openEdit(row)}
                          style={{ padding: 0, fontWeight: 500, textAlign: "left" }}
                        >
                          {row.name}
                        </button>
                      </td>
                      <td><span className="muted">{describeBlackoutSpec(row.spec)}</span></td>
                      <td><span className="muted">{row.description || "—"}</span></td>
                      <td>
                        <div className="adminListActions">
                          <button className="adminListAction" type="button" onClick={() => openEdit(row)}>Edit</button>
                          <button className="adminListAction isDanger" type="button" onClick={() => void del(row)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </div>

      {edit ? (
        <div className="modalOverlay" role="presentation" onClick={() => setEdit(null)}>
          <div
            className="modal modalScrollable"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bs-modal-title"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 580 }}
          >
            <div className="modalScrollHeader">
              <div className="reportModalTitle" id="bs-modal-title">
                {edit.id ? "Edit blackout schedule" : "New blackout schedule"}
              </div>
            </div>

            <div className="modalScrollBody" style={{ paddingTop: 12, paddingBottom: 12 }}>
              <div style={{ display: "grid", gap: 12 }}>
                <label className="field">
                  <span className="label">Name</span>
                  <input
                    className="input"
                    value={edit.name}
                    onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                    autoFocus
                    placeholder="e.g. Weekend maintenance"
                  />
                </label>
                <label className="field">
                  <span className="label">Description (optional)</span>
                  <input
                    className="input"
                    value={edit.description}
                    onChange={(e) => setEdit({ ...edit, description: e.target.value })}
                  />
                </label>
                <RecurrenceEditor
                  mode="blackout"
                  value={edit.recurrence}
                  onChange={(next) => setEdit({ ...edit, recurrence: next })}
                />
              </div>
            </div>

            <div className="modalScrollFooter">
              <div className="actions">
                <button className="btn" type="button" onClick={() => setEdit(null)} disabled={busy}>Cancel</button>
                <button
                  className="btn btnPrimary"
                  type="button"
                  onClick={() => void save()}
                  disabled={busy || !isValid(edit)}
                >
                  {edit.id ? "Save" : "Create"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </PageLayout>
  );
}
